import type { BrandSuggestion } from '@mg/shared';
import { useBrandDraft } from '../draft/BrandDraftContext';
import { isEmptyValue } from '../draft/brandDraft';
import { useRevisitField } from '../revisit/useRevisitField';
import type { WizardFieldRef } from '../steps';
import { useSuggestions } from './SuggestionsProvider';

export interface FieldSuggestionState {
  /** A suggestion is on offer for the field: its "AI suggested" tag shows (spec:124). */
  offered: boolean;
  /** What its box shows; null when nothing is offered. */
  suggestion: BrandSuggestion | null;
}

const NONE: FieldSuggestionState = { offered: false, suggestion: null };

/**
 * One field's AI suggestion (spec:113-125): on offer while the field is empty (spec:117), until it is
 * dismissed. A field with content shows none: Use suggestion filled it, or the customer typed. Never one
 * on an audience field (suggestions are for the brand fields: BrandSuggestionRequest.keys), nor while the
 * field is read-only (Revisit's Show changes, spec:134), nor while the flag file is off.
 */
export function useFieldSuggestion(field: WizardFieldRef): FieldSuggestionState {
  const suggestions = useSuggestions();
  const { state } = useBrandDraft();
  const { readOnly } = useRevisitField(field);
  if (!suggestions || field.kind !== 'field' || readOnly) return NONE;
  const suggestion = suggestions.offers[field.key];
  if (!suggestion || suggestions.dismissed.has(field.key) || !isEmptyValue(state.fields[field.key].value)) return NONE;
  return { offered: true, suggestion };
}
